import React, { useState, useEffect } from "react";
import {
  LabelWrap,
  HelpWrapper,
  HelpIcon,
  HelpTooltip,
  HelpTooltipText,
  StripeCardNumberInput,
  StripeCardExpiryInput,
  StripeCardCvcInput,
} from "./styles";
import {
  Wrapper,
  Label,
  ErrorMessage,
  Message,
} from "../TextField/TextField.styles";
import BackOfCardIcon from "@ecologi/assets/icons/back-of-card.svg";
import { capitaliseFirstLetter, InputVariantTypes } from "@ecologi/shared";

import { StripeElementChangeEvent } from "@stripe/stripe-js";

export type StripeError = {
  code: string;
  type: string;
  message: string;
};

export type StripeInputProps = {
  label: string;
  helpText?: string;
  isDisabled?: boolean;
  onChange?: (e: StripeElementChangeEvent) => void;
  variant?: InputVariantTypes;
};

export type StripeInputWrapperProps = {
  children: React.ReactNode;
  hasTooltip?: boolean;
  error: false | StripeError;
};

export const StripeInputWrapper = ({
  children,
  isDisabled,
  error,
  label,
  helpText,
  hasTooltip,
}: StripeInputProps & StripeInputWrapperProps) => {
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  useEffect(() => {
    // Hide tooltip after two seconds, can be changed.
    if (showTooltip) {
      setTimeout(() => setShowTooltip(false), 2000);
    }
  }, [showTooltip]);

  return (
    <Wrapper>
      <LabelWrap>
        <Label use="textInputLabel" as="label" isDisabled={isDisabled}>
          {capitaliseFirstLetter(label, true)}
        </Label>
        {hasTooltip && (
          <HelpWrapper
            showTooltip={showTooltip}
            onClick={() => setShowTooltip(true)}
          >
            <HelpIcon showTooltip={showTooltip} use="caption">
              ?
            </HelpIcon>
            <HelpTooltip showTooltip={showTooltip}>
              <BackOfCardIcon />
              <HelpTooltipText use="label" as="label">
                You should find this on the back of your card.
              </HelpTooltipText>
            </HelpTooltip>
          </HelpWrapper>
        )}
      </LabelWrap>
      {children}
      {error && error.message && (
        <ErrorMessage use="label" as="label">
          {error.message}
        </ErrorMessage>
      )}
      {!error && helpText && (
        <Message use="label" as="label">
          {helpText}
        </Message>
      )}
    </Wrapper>
  );
};

export const StripeCardNumber = ({
  onChange,
  variant = InputVariantTypes.DEFAULT,
  ...props
}: StripeInputProps) => {
  const [error, setError] = useState<false | StripeError>(false);
  const { isDisabled } = props;
  const handleChange = (event: StripeElementChangeEvent) => (
    onChange?.(event), setError(event.error as StripeError)
  );

  return (
    <StripeInputWrapper {...{ error, ...props }}>
      <StripeCardNumberInput
        variant={variant}
        isDisabled={isDisabled}
        options={{
          disabled: isDisabled,
          placeholder: "e.g. 0000 0000 0000 0000",
        }}
        isError={error}
        onChange={handleChange}
        {...props}
      />
    </StripeInputWrapper>
  );
};

export const StripeCardExpiry = ({
  onChange,
  variant = InputVariantTypes.DEFAULT,
  ...props
}: StripeInputProps) => {
  const [error, setError] = useState<false | StripeError>(false);
  const { isDisabled } = props;
  const handleChange = (event: StripeElementChangeEvent) => (
    onChange?.(event), setError(event.error as StripeError)
  );

  return (
    <StripeInputWrapper {...{ error, ...props }}>
      <StripeCardExpiryInput
        variant={variant}
        isDisabled={isDisabled}
        options={{
          disabled: isDisabled,
          placeholder: `e.g. 01/${(new Date().getFullYear() + 4)
            .toString()
            .slice(-2)}`,
        }}
        isError={error}
        onChange={handleChange}
        {...props}
      />
    </StripeInputWrapper>
  );
};

export const StripeCardCVC = ({
  onChange,
  variant = InputVariantTypes.DEFAULT,
  ...props
}: StripeInputProps) => {
  const [error, setError] = useState<false | StripeError>(false);
  const { isDisabled } = props;
  const handleChange = (event: StripeElementChangeEvent) => (
    onChange?.(event), setError(event.error as StripeError)
  );

  return (
    <StripeInputWrapper {...{ hasTooltip: true, error, ...props }}>
      <StripeCardCvcInput
        variant={variant}
        isDisabled={isDisabled}
        options={{ disabled: isDisabled, placeholder: "e.g. 123" }}
        isError={error}
        onChange={handleChange}
        {...props}
      />
    </StripeInputWrapper>
  );
};
